import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import API from '../util/API';
import { logout } from './userAuthSlice';
import { readAuthSession } from '../util/authSessionStorage';

const normalizeId = (value) => String(value ?? '').trim();
const uniqueList = (values) => Array.from(new Set(values.filter(Boolean)));

// Anonymous wishlist staging: hearts clicked while logged out are kept in
// localStorage and merged into the server list on first authenticated fetch.
const WISHLIST_PENDING_KEY = 'wishlist_pending';

const readPendingWishlist = () => {
  try {
    const raw = typeof window === 'undefined' ? null : window.localStorage.getItem(WISHLIST_PENDING_KEY);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((entry) => entry && normalizeId(entry.productId))
      : [];
  } catch {
    return [];
  }
};

const writePendingWishlist = (entries) => {
  try {
    if (typeof window === 'undefined') return;
    if (!entries.length) window.localStorage.removeItem(WISHLIST_PENDING_KEY);
    else window.localStorage.setItem(WISHLIST_PENDING_KEY, JSON.stringify(entries));
  } catch {
    // ignore storage failures (private mode, quota)
  }
};

const getWishlistProductId = (item) => {
  if (!item) return '';
  if (typeof item === 'string' || typeof item === 'number') return normalizeId(item);

  const productObject = item?.product && typeof item.product === 'object' ? item.product : null;
  const candidate =
    item?.productId ||
    item?.product_id ||
    item?.productCountryId ||
    productObject?.productId ||
    productObject?.product_id ||
    productObject?.id ||
    productObject?._id ||
    item?.product ||
    '';

  return normalizeId(candidate);
};

const hasAuthToken = (getState) => {
  const stateToken = normalizeId(getState?.()?.userAuth?.token);
  if (stateToken) return true;
  return Boolean(normalizeId(readAuthSession().token));
};

export const mergeWishlistPending = createAsyncThunk(
  'wishlist/mergeWishlistPending',
  async (_, { getState }) => {
    if (!hasAuthToken(getState)) return [];
    const pending = readPendingWishlist();
    if (!pending.length) return [];
    // Clear first so a slow/retried merge can never double-post the same ids.
    writePendingWishlist([]);
    const merged = [];
    for (const entry of pending) {
      const productId = normalizeId(entry.productId);
      try {
        const res = await API.post('/wishlist/add', { productId });
        merged.push(res?.data?.data || { productId });
      } catch {
        // Stale or deleted product: drop silently — the server list wins.
      }
    }
    return merged;
  }
);

export const addWishlistEntry = createAsyncThunk(
  'wishlist/addWishlistEntry',
  async (productId, { getState, dispatch, rejectWithValue }) => {
    const id = normalizeId(productId);
    if (!id) return rejectWithValue('Invalid wishlist item');

    if (!hasAuthToken(getState)) {
      const pending = readPendingWishlist();
      if (!pending.some((entry) => normalizeId(entry.productId) === id)) {
        pending.push({ productId: id, at: Date.now() });
        writePendingWishlist(pending);
      }
      toast.success('Saved! Sign in to keep this list.');
      return { productId: id, staged: true };
    }

    try {
      // A signed-in device may still hold anonymous staged items — flush
      // them once before this add so the lists converge immediately.
      await dispatch(mergeWishlistPending());
      const res = await API.post('/wishlist/add', { productId: id });
      const data = res?.data?.data;
      return data || { productId: id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { getState, dispatch, rejectWithValue }) => {
    if (!hasAuthToken(getState)) {
      // Anonymous visitors surface their locally staged ids (hearts stay
      // filled across reloads); nothing hits the server.
      return readPendingWishlist().map((entry) => ({ productId: normalizeId(entry.productId) }));
    }
    try {
      // Merge-then-fetch ordering: staged anonymous items land on the server
      // first, then the authoritative list replaces local state.
      await dispatch(mergeWishlistPending());
      const res = await API.get('/wishlist');
      return Array.isArray(res?.data?.data) ? res.data.data : [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch wishlist');
    }
  }
);

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (productId, { getState, rejectWithValue }) => {
    if (!hasAuthToken(getState)) {
      return rejectWithValue('Please sign in to save this item to your wishlist.');
    }

    try {
      const res = await API.post('/wishlist/add', { productId });
      const data = res?.data?.data;
      return data || { productId: normalizeId(productId) };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId, { getState, rejectWithValue }) => {
    if (!hasAuthToken(getState)) {
      // Anonymous 'unheart' of a staged item: drop it from local staging.
      const id = normalizeId(productId);
      writePendingWishlist(readPendingWishlist().filter((entry) => normalizeId(entry.productId) !== id));
      return { productId: id, staged: true };
    }

    try {
      const res = await API.delete('/wishlist/remove', { data: { productId } });
      const data = res?.data?.data;
      return data || { productId: normalizeId(productId) };
    } catch (err) {
      if (err?.response?.status === 404) {
        return { productId: normalizeId(productId) };
      }
      return rejectWithValue(err.response?.data?.message || 'Failed to remove from wishlist');
    }
  }
);

const initialState = {
  // Anonymous staged ids surface as wishlist items so hearts stay filled
  // across reloads until the account merge absorbs them.
  items: readPendingWishlist().map((entry) => normalizeId(entry.productId)),
  loading: false,
  error: null,
  status: 'idle',
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearWishlist(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        const list = Array.isArray(action.payload) ? action.payload : [];
        state.items = uniqueList(list.map(getWishlistProductId));
        state.status = 'succeeded';
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(addWishlistEntry.pending, (state) => {
        state.error = null;
      })
      .addCase(addWishlistEntry.fulfilled, (state, action) => {
        const incomingId = getWishlistProductId(action.payload) || normalizeId(action.meta?.arg);
        if (!incomingId) return;
        if (!state.items.includes(incomingId)) state.items.push(incomingId);
      })
      .addCase(addWishlistEntry.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(mergeWishlistPending.fulfilled, (state, action) => {
        const merged = Array.isArray(action.payload) ? action.payload : [];
        merged.forEach((entry) => {
          const id = getWishlistProductId(entry);
          if (id && !state.items.includes(id)) state.items.push(id);
        });
      })
      .addCase(addToWishlist.pending, (state) => {
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        const incomingId = getWishlistProductId(action.payload) || normalizeId(action.meta?.arg);
        if (!incomingId) return;
        if (!state.items.includes(incomingId)) state.items.push(incomingId);
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(removeFromWishlist.pending, (state) => {
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        const removedId = getWishlistProductId(action.payload) || normalizeId(action.meta?.arg);
        if (!removedId) return;
        state.items = state.items.filter((item) => normalizeId(item) !== removedId);
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(logout, () => ({
        // initialState.items is frozen at import time; re-read staging so a
        // logout in an already-open tab restores the correct anonymous set.
        ...initialState,
        items: readPendingWishlist().map((entry) => normalizeId(entry.productId)),
      }));
  },
});

export const { clearWishlist } = wishlistSlice.actions;
export default wishlistSlice.reducer;
